// idiomaAvisos.js — opciones de Mensajes automáticos de autoescuela del 8/10/2026 (Autoescola Garrotxa, Olot):
//   · idioma de los mensajes al alumno (c_autoescuela_config.idioma_mensajes: "es" | "ca", catalán central);
//   · recordatorio SOLO con «Confirmar práctica» (automatic_messages.recordatorio_practica.solo_confirmar);
//   · «Quiero cambiar algo» = CANCELAR (semana_cambio.modo "cancelar");
//   · cuándo sale la bienvenida (automatic_messages.bienvenida_alumno.momento).
// Ficha: docs/features/autoescuela-mensajes-catalan-y-cancelar.md
//
// ESPEJO del backend (lo fija __tests__/idiomaAvisos.mirror.test.js):
//   - PLANTILLAS_CA y RECORDATORIO_CONF_ES  ← admin_scripts/add_autoescuela_catalan_templates.js (lo creado en Meta)
//   - TEXTOS_CANCELAR y las filas de ejemplo ← aws_lambdas/c_processMessage/semanaBotones.js
//   - textos de respuesta en catalán         ← semanaBotones.js, i18nStrings.js, practicaCancelReply.js, c_offerPracticaSlot
//
// COPIA POR VALOR (fichero idéntico) en apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/idiomaAvisos.js
// — un test del simulador compara los dos ficheros. Funciones PURAS, sin red.

// Interruptores de front (app y simulador): a false, la opción se guarda pero la pantalla avisa de que aún no sale.
//  · Las 27 traducciones `ca` de las plantillas: APPROVED/UTILITY en Meta el 8/10/2026.
//  · practica_recordatorio_crm_trial_conf (es y ca): APPROVED/UTILITY el 8/10/2026; en el backend, PRACTICA_SOLO_CONFIRMAR=1.
// «Hasta cuándo puede anular el alumno» (26/10/2026): la línea de la lista es la del backend (espejo por marcas).
import { lineaCorteLista } from "./corteCancelacion";

export const CATALAN_DISPONIBLE = true;
export const SOLO_CONFIRMAR_DISPONIBLE = true;

export const IDIOMAS_MENSAJES = Object.freeze([
  { value: "es", label: "Castellano" },
  { value: "ca", label: "Català" },
]);

/** "ca" solo si la autoescuela lo tiene puesto de verdad (idiomaMensajes.idiomaDe del backend). */
export const idiomaDe = (cfg) => (cfg && cfg.idioma_mensajes === "ca" ? "ca" : "es");

/** botonesAviso.soloConfirmar del backend: solo con botones y solo_confirmar === true. */
export function soloConfirmar(am) {
  const c = am && typeof am === "object" ? am.recordatorio_practica : null;
  const conBotones = c && typeof c.con_botones === "boolean" ? c.con_botones : true;
  return !!(conBotones && c && c.solo_confirmar === true);
}

// ── Cuándo sale la bienvenida (bienvenidaAlumno.momentoBienvenida) ──────────────────────────────
export const MOMENTOS_BIENVENIDA = Object.freeze([
  { value: "alta", label: "Al dar de alta al alumno", help: "Le llega en cuanto lo guardas (también si se matricula online)." },
  { value: "primera_confirmacion", label: "Cuando confirma su primera práctica", help: "Le llega cuando pulsa «Confirmar práctica» o «Me viene bien» por primera vez." },
  // UNA sola bienvenida por alumno (9/10/2026): con «ambos» sale en el primero de los dos momentos que ocurra.
  { value: "ambos", label: "Al darlo de alta o, si entonces no se pudo, en su primera confirmación", help: "Se envía una sola vez: al darlo de alta o, si entonces no le llegó (sin móvil, o no marcaste la casilla de la bienvenida al crearlo), cuando confirma su primera práctica." },
]);
export const momentoBienvenida = (b) => (b && ["alta", "primera_confirmacion", "ambos"].includes(b.momento) ? b.momento : "alta");

// ── semana_cambio con el modo «cancelar» (flowCambiarPractica.semanaCambioConfig no lo conoce) ──
/** El modo tal cual lo guarda c_putAutoescuelaConfig: "texto" | "huecos" | "cancelar". */
export const modoCambio = (raw) => (raw && ["huecos", "cancelar"].includes(raw.modo) ? raw.modo : "texto");

// ── Plantillas en catalán (mismo nombre y estructura que las `es`, language "ca") ──────────────
export const PLANTILLAS_CA = {
  "practica_recordatorio_crm_trial": {
    "header": "RECORDATORI DE PRÀCTICA",
    "body": "📩 *{{1}}* t'envia aquest recordatori:\n\nHola {{2}},\n\nEt recordem que tens una pràctica el {{3}} a les {{4}}.\n\nEns confirmes que hi vindràs?",
    "footer": "Gràcies per la teva comprensió",
    "buttons": [
      "Confirmar pràctica",
      "Cancel·lar pràctica"
    ]
  },
  "practica_recordatorio_crm_trial_v2": {
    "header": "RECORDATORI DE PRÀCTICA",
    "body": "📩 *{{1}}* t'envia aquest recordatori:\n\nHola {{2}},\n\nEt recordem que tens una pràctica el {{3}} a les {{4}}.\n\nEns confirmes que hi vindràs?",
    "footer": "Gràcies per la teva comprensió",
    "buttons": [
      "Confirmar pràctica",
      "Canviar l'hora",
      "Cancel·lar pràctica"
    ]
  },
  "practica_recordatorio_crm_trial_sinbtn": {
    "header": "RECORDATORI DE PRÀCTICA",
    "body": "📩 *{{1}}* t'envia aquest recordatori:\n\nHola {{2}},\n\nEt recordem que tens una pràctica el {{3}} a les {{4}}.\n\nSi no pots venir, avisa'ns per aquest xat.",
    "footer": "Gràcies per la teva comprensió"
  },
  "practica_recordatorio_crm_trial_conf": {
    "header": "RECORDATORI DE PRÀCTICA",
    "body": "📩 *{{1}}* t'envia aquest recordatori:\n\nHola {{2}},\n\nEt recordem que tens una pràctica el {{3}} a les {{4}}.\n\nConfirma la teva assistència amb el botó. Si no pots venir, avisa'ns per aquest xat.",
    "footer": "Gràcies per la teva comprensió",
    "buttons": [
      "Confirmar pràctica"
    ]
  },
  "practica_asignada_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nT'hem assignat una pràctica de {{5}} el {{3}} a les {{4}}.\n\nT'esperem!",
    "footer": "Gràcies per la teva comprensió"
  },
  "practica_asignada_crm_trial_img": {
    "image": true,
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nT'hem assignat una pràctica de {{5}} el {{3}} a les {{4}}.\n\nT'esperem!"
  },
  "practica_asignada_crm_trial_btn": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nT'hem assignat una pràctica de {{5}} el {{3}} a les {{4}}.\n\nT'esperem!",
    "buttons": [
      "Em va bé",
      "Vull canviar alguna cosa"
    ]
  },
  "practica_asignada_crm_trial_img_btn": {
    "image": true,
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nT'hem assignat una pràctica de {{5}} el {{3}} a les {{4}}.\n\nT'esperem!",
    "buttons": [
      "Em va bé",
      "Vull canviar alguna cosa"
    ]
  },
  "practicas_semana_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}} 👋\n\nAquestes són les teves pràctiques programades per a aquesta setmana:\n\n{{3}}\n\nPer a qualsevol canvi o cancel·lació, contacta'ns per aquest xat.",
    "footer": "Gràcies per la teva comprensió"
  },
  "practicas_semana_crm_trial_img": {
    "image": true,
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}} 👋\n\nAquestes són les teves pràctiques programades per a aquesta setmana:\n\n{{3}}\n\nPer a qualsevol canvi o cancel·lació, contacta'ns per aquest xat."
  },
  "practicas_semana_crm_trial_btn": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}} 👋\n\nAquestes són les teves pràctiques programades per a aquesta setmana:\n\n{{3}}\n\nPer a qualsevol canvi o cancel·lació, contacta'ns per aquest xat.",
    "buttons": [
      "Em va bé",
      "Vull canviar alguna cosa"
    ]
  },
  "practicas_semana_crm_trial_img_btn": {
    "image": true,
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}} 👋\n\nAquestes són les teves pràctiques programades per a aquesta setmana:\n\n{{3}}\n\nPer a qualsevol canvi o cancel·lació, contacta'ns per aquest xat.",
    "buttons": [
      "Em va bé",
      "Vull canviar alguna cosa"
    ]
  },
  "practica_modificada_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva {{3}} prevista per al {{4}} a les {{5}} s'ha modificat.\n\n📅 Nova data: {{6}} a les {{7}}\n\nEns confirmes que hi vindràs?",
    "footer": "Gràcies per la teva comprensió",
    "buttons": [
      "Confirmar pràctica",
      "Cancel·lar pràctica"
    ]
  },
  "practica_modificada_crm_trial_sinbtn": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva {{3}} prevista per al {{4}} a les {{5}} s'ha modificat.\n\n📅 Nova data: {{6}} a les {{7}}\n\nSi no et va bé, avisa'ns per aquest xat.",
    "footer": "Gràcies per la teva comprensió"
  },
  "practica_cancel_crm_trial": {
    "header": "PRÀCTICA CANCEL·LADA",
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva pràctica del {{3}} a les {{4}} s'ha cancel·lat.\n\nMotiu: {{5}}\n\nPer reprogramar-la, contacta amb nosaltres.",
    "footer": "Gràcies per la teva comprensió"
  },
  "bienvenida_alumno_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nAlta registrada ✅\n\nHola {{2}}, la teva matrícula per al permís {{3}} està registrada.\n\nNorma de les pràctiques: {{4}}\n\nPer aquest xat rebràs les teves pràctiques i els seus recordatoris."
  },
  "practica_hueco_libre_trial": {
    "header": "PRÀCTICA DISPONIBLE",
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nTenim una pràctica de {{3}} reservada per al {{4}} a les {{5}} amb {{6}}.\n\nConfirma'ns si la vols i quedarà al teu nom.",
    "buttons": [
      "La vull",
      "Ara no"
    ]
  },
  "practica_no_confirmada_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nPràctica anul·lada per falta de confirmació\n\nHola {{2}}, la teva pràctica del {{3}} a les {{4}} queda anul·lada perquè no estava confirmada {{5}} abans. No s'aplica cap càrrec.\n\nPer reprogramar-la, escriu-nos per aquest xat."
  },
  "horario_manana_profesor_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHorari de demà a punt ✅\n\nHola {{2}}, demà {{3}} tens {{4}}.\n\nPrem el botó per veure cada pràctica amb l'hora, l'alumne i el cotxe, i avisar si no pots fer alguna classe.",
    "cta": "Veure el meu horari"
  },
  "practica_cargo_cancelacion_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva pràctica del {{3}} a les {{4}} s'ha cancel·lat amb menys de {{5}} hores d'antelació.\n\nSegons les condicions del centre, s'ha afegit un càrrec de {{6}} al teu compte. Pots consultar-ho amb nosaltres per aquest xat."
  },
  "practica_cancelacion_cobrada_trial": {
    "body": "📩 *{{1}}* t'informa:\n\n✅ Pagament rebut. Hola {{2}}, la teva pràctica del {{3}} a les {{4}} es va cancel·lar amb menys de {{5}} hores d'antelació i, segons l'autorització que vas signar en matricular-te, hem cobrat {{6}} a la teva targeta desada. Si tens cap dubte, respon aquest missatge."
  },
  "examen_recordatorio_crm_trial_v2": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}}, et recordem que tens l'examen {{3}} el {{4}}{{5}}.\n\nRecorda portar el DNI i arribar amb temps. Molta sort!",
    "footer": "Gràcies per la teva confiança"
  },
  "examen_resultado_crm_trial_v2": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}}, ja tenim el resultat del teu examen de conduir {{3}}. El resultat ha estat: *{{4}}*. Per a qualsevol dubte, no dubtis a contactar amb nosaltres.",
    "footer": "Gràcies per la teva confiança"
  },
  "enlace_pago_crm_trial_v2": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}}, aquí tens l'enllaç per pagar {{3}}. Prem el botó per completar el pagament de manera segura.",
    "footer": "Gràcies per la teva confiança",
    "cta": "Pagar ara"
  },
  "semana_profesor_crm_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nPlanificació setmanal a punt ✅\n\nHola {{2}}, la teva setmana del {{3}} al {{4}} té {{5}} en total.\n\nPrem el botó per veure cada pràctica amb l'hora, l'alumne i el cotxe, i avisar si algun dia no pots fer classe.",
    "cta": "Veure la meva setmana"
  },
  "factura_alumno_autoescuela_trial": {
    "document": true,
    "body": "T'escriu *{{1}}*.\n\nHola {{2}}, aquí tens la teva factura {{3}}. Gràcies per confiar en nosaltres per treure't el carnet. 🚗",
    "footer": "Conextcrm · Facturació"
  },
  "practica_peticion_no_atendida_trial": {
    "body": "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nNo t'hem pogut assignar la classe pràctica que vas demanar.\n\nMotiu: {{3}}\n\nEscriu-nos per aquí i buscarem un altre forat."
  }
};
export const RECORDATORIO_CONF_ES = {
  "header": "RECORDATORIO DE PRÁCTICA",
  "body": "📩 *{{1}}* te envía este recordatorio:\n\nHola {{2}},\n\nLe recordamos que tiene una práctica el {{3}} a las {{4}}.\n\nConfirme su asistencia con el botón. Si no puede venir, avísenos por este chat.",
  "footer": "Gracias por tu comprensión",
  "buttons": [
    "Confirmar práctica"
  ]
};

/** Rellena una plantilla catalana: { header, message, footer, buttons, cta }. `vals` = [{{1}}, {{2}}, …]. */
export function plantillaCa(name, vals = []) {
  const p = PLANTILLAS_CA[name];
  if (!p) return null;
  const message = p.body.replace(/\{\{(\d+)\}\}/g, (m, n) => (vals[Number(n) - 1] != null ? String(vals[Number(n) - 1]) : m));
  return {
    ...(p.header ? { header: p.header } : {}),
    message,
    ...(p.footer ? { footer: p.footer } : {}),
    ...(p.buttons ? { buttons: [...p.buttons] } : {}),
    cta: p.cta || null,
  };
}

// Etiquetas de los botones en catalán → la de castellano (c_sendTemplate pone la `es` como payload, así que el
// backend las trata igual).
export const BOTON_CA = Object.freeze({
  "Confirmar pràctica": "Confirmar práctica",
  "Cancel·lar pràctica": "Cancelar práctica",
  "Canviar l'hora": "Cambiar hora",
  "La vull": "Lo quiero",
  "Ara no": "Ahora no",
  "Em va bé": "Me viene bien",
  "Vull canviar alguna cosa": "Quiero cambiar algo",
  "Pagar ara": "Pagar ahora",
});
export const botonEs = (label) => BOTON_CA[label] || label;

// ── Respuestas de texto en catalán (c_processMessage / c_offerPracticaSlot) ─────────────────────
export const PRACTICA_CONFIRMADA_CA = "Perfecte! La teva pràctica ha quedat confirmada. T'esperem el dia i l'hora acordats.";
export function practicaCancelReplyCa(phoneFmt) {
  return phoneFmt
    ? `Hem cancel·lat la teva pràctica. Si vols canviar-la per un altre dia, truca'ns o escriu-nos al ${phoneFmt}.`
    : "Hem cancel·lat la teva pràctica. Si vols canviar-la per un altre dia, escriu-nos per aquí i et buscarem un altre forat.";
}
export const SEMANA_CA = {
  meVieneBien: (nombre) => `Perfecte, ${nombre}! Queda confirmada la teva setmana. Et recordarem cada pràctica abans de la classe.`,
  quieroCambiar: "D'acord. Escriu-nos aquí què vols canviar (per exemple: «dimarts no puc, pot ser dimecres a la tarda?») i l'autoescola et respondrà.",
  recibido: "Rebut. Ho passem a l'autoescola i et respondran per aquí.",
  ejemplo: "Dimarts no puc, pot ser dimecres a la tarda?",
};
export const ASIGNADA_CA = {
  meVieneBien: (nombre, cuando) => `Perfecte, ${nombre}! Queda confirmada la teva pràctica${cuando ? ` del ${cuando}` : ""}. Te la recordarem abans de la classe.`,
  quieroCambiar: "D'acord. Escriu-nos aquí què vols canviar (per exemple: «a aquesta hora no puc, pot ser a la tarda?») i l'autoescola et respondrà.",
  ejemplo: "A aquesta hora no puc, pot ser a la tarda?",
};
export const HUECO_CA = {
  ganada: "Fet! La pràctica queda al teu nom. T'esperem.",
  otro: "Ho sentim, un altre alumne ja s'ha quedat aquesta pràctica. T'avisarem tan bon punt se n'alliberi una altra.",
  plazo: "Aquesta pràctica ja no està disponible: el termini per agafar-la ha passat. T'avisarem tan bon punt se n'alliberi una altra.",
};

// ── «Quiero cambiar algo» = cancelar (semanaBotones.TEXTOS_CANCELAR y buildListaCancelar) ────────
export const TEXTOS_CANCELAR = {
  es: {
    cuerpo: "Elige qué práctica quieres cancelar. La autoescuela recibirá el aviso.",
    tardia: (h) => `Si la cancelas con menos de ${h} ${h === 1 ? "hora" : "horas"} de antelación, se cobra según las condiciones de la autoescuela.`,
    boton: "Elegir práctica",
    ninguna: "No cancelar ninguna",
    nadaCancelado: "De acuerdo, no se cancela nada. Tus prácticas siguen igual.",
    hecho: (cuando) => `Hecho: tu práctica${cuando ? ` del ${cuando}` : ""} queda cancelada. Ya se lo hemos dicho a la autoescuela.`,
  },
  ca: {
    cuerpo: "Tria quina pràctica vols cancel·lar. L'autoescola en rebrà l'avís.",
    tardia: (h) => `Si la cancel·les amb menys de ${h} ${h === 1 ? "hora" : "hores"} d'antelació, es cobra segons les condicions de l'autoescola.`,
    boton: "Triar pràctica",
    ninguna: "No en cancel·lo cap",
    nadaCancelado: "D'acord, no es cancel·la res. Les teves pràctiques continuen igual.",
    hecho: (cuando) => `Fet: la teva pràctica${cuando ? ` del ${cuando}` : ""} queda cancel·lada. Ja ho hem dit a l'autoescola.`,
  },
};
// Las prácticas de ejemplo del aviso (martes 6/10 17:00 y sábado 10/10 10:00, hora de Madrid): título de la fila
// (tituloFilaPractica) y cómo se nombra en la respuesta (cuandoPractica).
export const FILAS_CANCELAR = {
  es: [{ title: "Mar 6/10 17:00", cuando: "martes 6/10 a las 17:00" }, { title: "Sáb 10/10 10:00", cuando: "sábado 10/10 a las 10:00" }],
  ca: [{ title: "Dt 6/10 17:00", cuando: "dimarts 6/10 a les 17:00" }, { title: "Ds 10/10 10:00", cuando: "dissabte 10/10 a les 10:00" }],
};
const langDe = (lang) => (lang === "ca" ? "ca" : "es");

/** Horas de la cancelación tardía que COBRA (semanaBotones.horasTardiaQueCobra); null si no cobra. */
export function horasTardiaQueCobra(lc) {
  if (!lc || lc.enabled !== true || lc.mode !== "charge") return null;
  const h = Math.round(Number(lc.hours));
  return Number.isFinite(h) && h > 0 ? h : null;
}

/** La lista que recibe el alumno: { texto, boton, filas: [títulos], } (una sola práctica en la asignada). */
// `corte` (26/10/2026): horas del corte de cancelación del alumno; con él, la lista avisa (semanaBotones.buildListaCancelar).
export function listaCancelar({ lang, una = false, tardia = null, corte = 0 } = {}) {
  const l = langDe(lang);
  const tx = TEXTOS_CANCELAR[l];
  const h = horasTardiaQueCobra(tardia);
  const filas = (una ? FILAS_CANCELAR[l].slice(0, 1) : FILAS_CANCELAR[l]).map((f) => f.title);
  const texto = [tx.cuerpo, h ? tx.tardia(h) : null, corte > 0 ? lineaCorteLista(corte, l) : null].filter(Boolean).join("\n\n");
  return { texto, boton: tx.boton, filas: [...filas, tx.ninguna] };
}

/** ¿Es una fila de la lista de cancelar? → { lang, ninguna, cuando } | null. */
export function filaCancelar(label) {
  for (const l of ["es", "ca"]) {
    if (label === TEXTOS_CANCELAR[l].ninguna) return { lang: l, ninguna: true };
    const f = FILAS_CANCELAR[l].find((x) => x.title === label);
    if (f) return { lang: l, ninguna: false, cuando: f.cuando };
  }
  return null;
}
